import { orderBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    bind,
    displayFormatTime,
    Duration,
    Enums,
    formatDuration,
    getDurationToNow,
    HelpTooltip,
    slugCase,
} from 'Mri.Web.Common';
import * as React from 'react';

export class DisplayEventGroup {
    constructor(model: Mri.DisplayEvents.DisplayEvent | Mri.Web.Portal.Controllers.Displays.DisplayEventModel) {
        if (isDisplayGroupModel(model)) {
            this.head = model;
            this.displayEvents = undefined;
            this.numDisplayEvents = model.count;
        } else {
            this.head = model;
            this.displayEvents = [];
        }
    }

    @bind
    @action
    public toggle(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.open = !this.open;
    }

    @computed
    public get count() {
        if (this.displayEvents) return this.displayEvents.length;
        else return this.numDisplayEvents ?? 0;
    }

    @computed
    public get canOpen() {
        return this.displayEvents != undefined;
    }

    @observable
    public head: Mri.DisplayEvents.DisplayEvent;
    @observable
    public displayEvents?: Mri.DisplayEvents.DisplayEvent[];
    @observable
    public open = false;

    @observable
    private numDisplayEvents?: number;
}

export interface IDisplayEventsList {
    renderRow?(
        displayEvent: Mri.DisplayEvents.DisplayEvent,
        group: DisplayEventGroup,
        isHead: boolean
    ): React.ReactNode;
    readonly displayEvents:
    | Mri.DisplayEvents.DisplayEvent[]
    | Mri.Web.Portal.Controllers.Displays.DisplayEventModel[];
    readonly serialNumber: string;
}

@observer
export class DisplayEventsList extends React.Component<IDisplayEventsList> {
    public render() {
        return <div className="row">{this.groupedDisplayEvents.map(this.renderHeadRow)}</div>;
    }

    @bind
    private renderHeadRow(group: DisplayEventGroup) {
        return (
            <div key={group.head.displayEventGuid} className="col-12 mb-3">
                <li className="list-group-item">
                    {this.renderRow(group.head, group, true)}
                    {group.canOpen && group.count > 0 && (
                        <a
                            onClick={group.toggle}
                            className={`list-group-item-text ${this.props.renderRow ? 'ps-5' : ''}`}
                        >
                            {group.count} &nbsp; Similar Display Events <b className="caret" />
                        </a>
                    )}
                </li>
                {group.open &&
                    group.displayEvents?.map(de => (
                        <li className="list-group-item ms-3" key={de.displayEventGuid}>
                            {this.renderRow(de, group, false)}
                        </li>
                    ))}
            </div>
        );
    }

    @bind
    private renderRow(displayEvent: Mri.DisplayEvents.DisplayEvent, group: DisplayEventGroup, isHead: boolean) {
        const startTime = displayEvent.downtimeIncidentStartTime ?? displayEvent.creationTime;
        const duration =
            displayEvent.downtimeIncidentDuration != null
                ? new Duration(displayEvent.downtimeIncidentDuration * 1000)
                : displayEvent.downtimeActiveIncident
                    ? getDurationToNow(startTime)
                    : undefined;

        return (
            <div className="d-flex align-items-stretch">
                {this.props.renderRow && this.props.renderRow(displayEvent, group, isHead)}

                <div className="d-inline-block w-100">
                    <div>
                        <HelpTooltip
                            subjects={['Downtime Incidents']}
                            name={slugCase(
                                Enums.toString(Enums.DowntimeIncidentType, displayEvent.downtimeIncidentType)
                            )}
                            underline
                            placement="top"
                            className={this.eventSeverityStyle(displayEvent)}
                        >
                            <b className={this.eventSeverityStyle(displayEvent)}>{displayEvent.details}</b>
                        </HelpTooltip>

                        <div className="float-end">
                            <div className={this.incidentClass(displayEvent) + ' me-1'}>
                                {Enums.toString(Enums.DisplayIncidentLevel, displayEvent.incidentLevel)}
                            </div>

                            <div className={this.badgeClass(displayEvent) + ' me-1'}>
                                {Enums.toString(Enums.DisplayEventStatus, displayEvent.displayEventStatus)}
                            </div>

                            {displayEvent.downtimeActiveIncident && <div className="badge bg-warning">Active</div>}

                            {!displayEvent.downtimeActiveIncident && <div className="badge bg-secondary">Inactive</div>}
                        </div>
                    </div>

                    <p className="list-group-item-text mb-0 mt-1 f-14">
                        {Enums.toString(Enums.DowntimeIncidentType, displayEvent.downtimeIncidentType)}
                    </p>

                    {duration != null && (
                        <p className="list-group-item-text mb-0">
                            <b>Duration: </b>
                            {duration != null ? formatDuration(duration) : '-'}
                        </p>
                    )}

                    {startTime && (
                        <p className="list-group-item-text mb-0">
                            <b>Started: </b>
                            {displayFormatTime(startTime, 'EEEE, MMMM do yyyy, h:mm:ss a zzz', this.props.serialNumber)}
                        </p>
                    )}

                    {!group.canOpen && group.count > 0 && <span>{group.count} Similar Display Events</span>}
                </div>
            </div>
        );
    }

    @bind
    @action
    private badgeClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';
        if (displayEvent.displayEventStatus == Mri.DisplayEvents.DisplayEventStatus.Alert) {
            return badge + 'bg-warning';
        }
        return badge + 'bg-secondary';
    }

    @bind
    @action
    private incidentClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';

        let color = '';

        switch (displayEvent.incidentLevel) {
            default:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                color = 'bg-warning';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                color = 'bg-teal';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                color = 'bg-secondary';
                break;
        }

        return badge + color;
    }

    @computed
    private get groupedDisplayEvents(): DisplayEventGroup[] {
        if (this.props.displayEvents.length == 0) return [];

        let groups: DisplayEventGroup[];

        if (isDisplayGroupModels(this.props.displayEvents)) {
            const sortedDisplayEventGroups = orderBy(
                this.props.displayEvents,
                [
                    de => de.downtimeActiveIncident,
                    de => de.incidentLevel,
                    de => de.downtimeIncidentStartTime,
                    de => de.creationTime,
                ],
                ['desc', 'asc', 'desc', 'desc']
            );

            groups = sortedDisplayEventGroups.map(de => new DisplayEventGroup(de));
        } else {
            groups = [];

            const sortedDisplayEvents = orderBy(
                this.props.displayEvents,
                [
                    de => de.downtimeActiveIncident,
                    de => de.incidentLevel,
                    de => de.downtimeIncidentStartTime,
                    de => de.creationTime,
                ],
                ['desc', 'asc', 'desc', 'desc']
            );
            for (const displayEvent of sortedDisplayEvents) {
                let maxPercentage = 0;
                let bestFitGroup: DisplayEventGroup | undefined;
                for (const group of groups) {
                    if (
                        group.head.downtimeIncidentType == displayEvent.downtimeIncidentType &&
                        group.head.displayEventStatus == displayEvent.displayEventStatus
                    ) {
                        const percentage = similarity(group.head.details, displayEvent.details || 'UNDEFINED');

                        if (percentage > 0.77) {
                            if (percentage > maxPercentage) {
                                maxPercentage = percentage;
                                bestFitGroup = group;
                            }
                        }
                    }
                }

                if (bestFitGroup) {
                    bestFitGroup.displayEvents!.push(displayEvent);
                } else {
                    const group = new DisplayEventGroup(displayEvent);
                    groups.push(group);
                }
            }
        }

        groups = orderBy(
            groups,
            [
                g => g.head.downtimeActiveIncident,
                g => g.head.incidentLevel,
                g => g.head.downtimeIncidentStartTime,
                g => g.head.creationTime,
            ],
            ['desc', 'asc', 'desc', 'desc']
        );

        return groups;
    }

    @bind
    @action
    private eventSeverityStyle(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        let color = '';
        if (displayEvent.downtimeActiveIncident) {
            switch (displayEvent.incidentLevel) {
                default:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                    color = 'text-warning';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                    color = 'text-teal';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                    color = 'text-muted';
                    break;
            }
        } else color = 'text-muted';

        return color;
    }
}

// http://stackoverflow.com/questions/10473745/compare-strings-javascript-return-of-likely
function similarity(s1: string, s2: string) {
    let longer = s1;
    let shorter = s2;
    if (s1.length < s2.length) {
        longer = s2;
        shorter = s1;
    }
    const longerLength = longer.length;
    if (longerLength == 0) {
        return 1.0;
    }
    return (longerLength - editDistance(longer, shorter)) / longerLength;
}

function editDistance(s1: string, s2: string) {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();

    const costs = [];
    for (let ii = 0; ii <= s1.length; ii++) {
        let lastValue = ii;
        for (let jj = 0; jj <= s2.length; jj++) {
            if (ii == 0) costs[jj] = jj;
            else {
                if (jj > 0) {
                    let newValue = costs[jj - 1];
                    if (s1.charAt(ii - 1) != s2.charAt(jj - 1))
                        newValue = Math.min(Math.min(newValue, lastValue), costs[jj]) + 1;
                    costs[jj - 1] = lastValue;
                    lastValue = newValue;
                }
            }
        }
        if (ii > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
}

function isDisplayGroupModels(
    models: Mri.DisplayEvents.DisplayEvent[] | Mri.Web.Portal.Controllers.Displays.DisplayEventModel[]
): models is Mri.Web.Portal.Controllers.Displays.DisplayEventModel[] {
    const groups = models as Mri.Web.Portal.Controllers.Displays.DisplayEventModel[];
    return groups[0].count != undefined;
}

function isDisplayGroupModel(
    model: Mri.DisplayEvents.DisplayEvent | Mri.Web.Portal.Controllers.Displays.DisplayEventModel
): model is Mri.Web.Portal.Controllers.Displays.DisplayEventModel {
    const group = model as Mri.Web.Portal.Controllers.Displays.DisplayEventModel;
    return group.count != undefined;
}
